import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  App,
  Alert,
  Button,
  Descriptions,
  Drawer,
  Input,
  List,
  Modal,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  LayoutOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  getScreenConfigVersion,
  getScreenTemplate,
  listScreenConfigs,
  listScreenTemplates,
  type ScreenConfigSummary,
  type ScreenTemplateSummary,
  type ScreenConfigView,
} from '@/api/screens';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { EditorDrawer, type EditorSeed } from '@/screens/EditorDrawer';
import { PublishModal } from '@/screens/PublishModal';
import { RoomPreview } from '@/screens/RoomPreview';

const { Text, Paragraph } = Typography;

function statusTag(status: string) {
  const color =
    status === 'PUBLISHED' ? 'success' : status === 'DRAFT' ? 'processing' : 'default';
  return <Tag color={color}>{status}</Tag>;
}

/**
 * P3 — the screen studio (`/v1/screens**`): the vendor implementation team's surface for the
 * customer-facing journey screens. List every config version, author a draft in the builder (the
 * compile gate's diagnostics render on the control concerned), preview it in the real room, publish (signs Ed25519 + freezes the row; exactly one PUBLISHED per config). The nav
 * gates visually on `screens.read` roles; the API is the real enforcer.
 */
export function ScreenStudioPage() {
  usePageTitle('Screen Studio');
  const { message } = App.useApp();
  const [rows, setRows] = useState<ScreenConfigSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detail, setDetail] = useState<ScreenConfigView | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [editorSeed, setEditorSeed] = useState<EditorSeed | null>(null);

  // `?new=1` opens a blank draft — the URL seam the command palette's "New screen config" uses.
  // The parameter is consumed immediately so a refresh or a back-navigation does not re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setEditorSeed({});
    searchParams.delete('new');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // The template library (OL-24). `?template=` is how the flow gallery hands one over.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState<ScreenTemplateSummary[]>([]);
  useEffect(() => {
    if (!libraryOpen) return;
    listScreenTemplates()
      .then(setLibrary)
      .catch((e) => message.error(normalizeApiError(e).message));
  }, [libraryOpen, message]);
  const startFromLibrary = useCallback(
    (templateId: string) => {
      getScreenTemplate(templateId)
        .then((t) => {
          setLibraryOpen(false);
          // A copy under a name the author chooses, compiled like any other draft.
          setEditorSeed({ definition: JSON.stringify(t.definition) });
        })
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [message],
  );
  useEffect(() => {
    const templateId = searchParams.get('template');
    if (!templateId) return;
    startFromLibrary(templateId);
    searchParams.delete('template');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, startFromLibrary]);

  const [publishTarget, setPublishTarget] = useState<{ configId: string; version: number } | null>(
    null,
  );

  // Template-start: seed a brand-new config from any existing version's definition.
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    listScreenConfigs()
      .then((r) => {
        setRows(r);
        setUnavailable(false);
      })
      .catch((e) => {
        const err = normalizeApiError(e);
        if (isNotAvailable(err)) setUnavailable(true);
        else {
          setError(err.message);
          setRows([]);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDetail = useCallback(
    (row: ScreenConfigSummary) => {
      getScreenConfigVersion(row.configId, row.version)
        .then((v) => {
          setDetail(v);
          setDetailOpen(true);
        })
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [message],
  );

  const openNewDraftFrom = useCallback(
    (row: ScreenConfigSummary) => {
      getScreenConfigVersion(row.configId, row.version)
        .then((v) =>
          setEditorSeed({
            configId: v.configId,
            flowId: v.flowId ?? undefined,
            definition: v.definition,
          }),
        )
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [message],
  );

  /** "Start from existing": copy any version's definition into a NEW, unnamed config draft. */
  const startFromTemplate = useCallback(
    (row: ScreenConfigSummary) => {
      getScreenConfigVersion(row.configId, row.version)
        .then((v) => {
          setTemplateOpen(false);
          // No configId in the seed: this is a NEW config the author names — never a new
          // version of the template it was copied from.
          setEditorSeed({ definition: v.definition });
        })
        .catch((e) => message.error(normalizeApiError(e).message));
    },
    [message],
  );

  const templateRows = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (r) =>
            r.configId.toLowerCase().includes(q) || (r.flowId ?? '').toLowerCase().includes(q),
        )
      : rows;
    // Grouped by config, newest version first — the top row per config is the natural pick.
    return filtered
      .slice()
      .sort((a, b) =>
        a.configId === b.configId
          ? b.version - a.version
          : a.configId.localeCompare(b.configId),
      );
  }, [rows, templateSearch]);

  const previewSource = useMemo(() => ({ version: detail?.version ?? 0 }), [detail?.version]);

  const columns: ColumnsType<ScreenConfigSummary> = useMemo(
    () => [
      {
        title: 'Config',
        dataIndex: 'configId',
        render: (v: string) => <Text className="ol-mono" style={{ fontSize: 12.5 }}>{v}</Text>,
        sorter: (a, b) => a.configId.localeCompare(b.configId),
      },
      { title: 'v', dataIndex: 'version', width: 56, align: 'right' },
      { title: 'Status', dataIndex: 'status', width: 120, render: statusTag },
      {
        title: 'Flow',
        dataIndex: 'flowId',
        render: (v: string | null) =>
          v ? (
            <Text className="ol-mono" style={{ fontSize: 12 }}>{v}</Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Locales',
        dataIndex: 'locales',
        width: 110,
        render: (v: string[]) => (v ?? []).map((l) => <Tag key={l}>{l}</Tag>),
      },
      {
        title: 'Integrity',
        dataIndex: 'contentHash',
        render: (v: string | null, row) =>
          v ? (
            <Tooltip title={`${v} — signed at publish; re-verified whenever an invite is minted from it`}>
              <Tag icon={<SafetyCertificateOutlined />} color="success" style={{ fontSize: 11 }}>
                {v.slice(0, 16)}…
              </Tag>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.status === 'DRAFT' ? 'unsigned draft' : '—'}
            </Text>
          ),
      },
      {
        title: 'Published',
        key: 'published',
        render: (_, row) =>
          row.publishedAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.publishedAt.slice(0, 19).replace('T', ' ')} · {row.publishedBy}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        title: '',
        key: 'actions',
        width: 240,
        render: (_, row) => (
          <Space size={4}>
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => openDetail(row)}>
              View
            </Button>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => openNewDraftFrom(row)}
            >
              New draft
            </Button>
            {row.status === 'DRAFT' && (
              <Button
                size="small"
                type="primary"
                ghost
                icon={<SendOutlined />}
                onClick={() => setPublishTarget({ configId: row.configId, version: row.version })}
              >
                Publish
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [openDetail, openNewDraftFrom],
  );

  if (unavailable) {
    return (
      <EmptyState
        icon={<LayoutOutlined />}
        title="Screen Studio"
        description="Author the screens an applicant walks through in the dataroom — versioned, compile-gated, signed at publish. This deployment does not expose the screens API yet."
        bullets={[
          'Build a journey from the ten step kinds, per-step wording, header and progress.',
          'Validate renders every gate diagnostic at once, JSON-pointer-addressed.',
          'Publish signs (Ed25519) and freezes the version; the digest is re-verified whenever an invite is minted from it.',
        ]}
      />
    );
  }

  return (
    <div className="ol-page">
      <PageHeader
        title="Screen Studio"
        icon={<LayoutOutlined />}
        subtitle="The journey screens an applicant sees in the dataroom. A config is a governed artifact: drafts are versioned, the compile gate refuses errors, publish signs and freezes — exactly one PUBLISHED version per config is live, and new invites use it."
        extra={
          <>
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading} aria-label="Refresh" />
            <Tooltip title="Start from a ready journey for a flow template">
              <Button
                icon={<AppstoreOutlined />}
                data-testid="screens-library"
                onClick={() => setLibraryOpen(true)}
              >
                From a template
              </Button>
            </Tooltip>
            <Tooltip title="Seed a new config from an existing journey's definition">
              <Button
                icon={<CopyOutlined />}
                disabled={rows.length === 0}
                onClick={() => {
                  setTemplateSearch('');
                  setTemplateOpen(true);
                }}
              >
                Start from existing
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditorSeed({})}>
              New config
            </Button>
          </>
        }
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Table
          size="small"
          rowKey={(r) => `${r.configId}:${r.version}`}
          columns={columns}
          dataSource={rows}
          loading={loading && rows.length > 0}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{
            emptyText: loading ? (
              <TableSkeleton label="Loading screen configs" rows={4} />
            ) : error ? (
              <ErrorState
                what="this hub's screen configs"
                detail={error}
                onRetry={refresh}
                compact
              />
            ) : (
              <EmptyState
                compact
                icon={<LayoutOutlined />}
                title="No screen configs yet"
                description="A screen config is the journey an applicant walks through in the dataroom, versioned and signed at publish. Author the first one, or copy an existing journey as a starting point."
                action={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditorSeed({})}>
                    New config
                  </Button>
                }
              />
            ),
          }}
        />
      </Space>

      {/* Version detail: provenance + integrity + the journey it renders. */}
      <Drawer
        title={
          detail && (
            <Space>
              <Text className="ol-mono">{detail.configId}</Text>
              <Tag>v{detail.version}</Tag>
              {statusTag(detail.status)}
            </Space>
          )
        }
        width={760}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        extra={
          detail && (
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  setDetailOpen(false);
                  setEditorSeed({
                    configId: detail.configId,
                    flowId: detail.flowId ?? undefined,
                    definition: detail.definition,
                  });
                }}
              >
                New draft from this
              </Button>
              {detail.status === 'DRAFT' && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => {
                    setDetailOpen(false);
                    setPublishTarget({ configId: detail.configId, version: detail.version });
                  }}
                >
                  Publish
                </Button>
              )}
            </Space>
          )
        }
      >
        {detail && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Flow">{detail.flowId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Locales">{detail.locales.join(', ')}</Descriptions.Item>
              <Descriptions.Item label="Created">
                {detail.createdAt?.slice(0, 19).replace('T', ' ')} · {detail.createdBy}
              </Descriptions.Item>
              <Descriptions.Item label="Published">
                {detail.publishedAt
                  ? `${detail.publishedAt.slice(0, 19).replace('T', ' ')} · ${detail.publishedBy}`
                  : '—'}
              </Descriptions.Item>
              {detail.contentHash && (
                <Descriptions.Item label="Content hash" span={2}>
                  <Text className="ol-mono" style={{ fontSize: 11.5 }}>
                    {detail.contentHash}
                  </Text>
                </Descriptions.Item>
              )}
              {detail.signature && (
                <Descriptions.Item label="Signature" span={2}>
                  <Text className="ol-mono" style={{ fontSize: 11.5 }}>
                    {detail.signatureAlgorithm} · {detail.signatureKeyId}
                  </Text>
                </Descriptions.Item>
              )}
              {detail.publishNote && (
                <Descriptions.Item label="Publish note" span={2}>
                  {detail.publishNote}
                </Descriptions.Item>
              )}
            </Descriptions>

            {detail.status === 'PUBLISHED' && (
              <Alert
                type="info"
                showIcon
                message="Frozen by the ledger"
                description="A published version can never change, and its digest is re-verified whenever an invite is minted from it. To evolve the journey, cut a new draft from this version and publish it. Invites already sent keep the journey they were minted from."
              />
            )}

            <Tabs
              destroyInactiveTabPane
              items={[
                {
                  key: 'preview',
                  label: 'Preview in the room',
                  children: (
                    <RoomPreview
                      configId={detail.configId}
                      source={previewSource}
                      locales={detail.locales}
                    />
                  ),
                },
                {
                  key: 'definition',
                  label: 'Definition (canonical)',
                  children: (
                    <Input.TextArea
                      readOnly
                      value={prettyOrRaw(detail.definition)}
                      autoSize={{ minRows: 12, maxRows: 26 }}
                      className="ol-mono" style={{ fontSize: 12 }}
                    />
                  ),
                },
              ]}
            />
          </Space>
        )}
      </Drawer>

      {/* Authoring drawer — a fresh mount per seed so the buffer starts clean. */}
      {editorSeed !== null && (
        <EditorDrawer
          seed={editorSeed}
          open
          onClose={() => setEditorSeed(null)}
          onSaved={(view) => {
            setEditorSeed(null);
            refresh();
            setDetail(view);
            setDetailOpen(true);
          }}
        />
      )}

      {/* Publish confirmation: the act that signs, freezes and goes live for applicants. */}
      <PublishModal
        target={publishTarget}
        onClose={() => setPublishTarget(null)}
        onPublished={(view) => {
          setPublishTarget(null);
          refresh();
          setDetail(view);
          setDetailOpen(true);
        }}
      />

      {/* The template library: bundled journeys, by the flow template they fit. */}
      <Modal
        title={
          <Space>
            <AppstoreOutlined />
            <span>Start from a template</span>
          </Space>
        }
        open={libraryOpen}
        onCancel={() => setLibraryOpen(false)}
        footer={null}
        width={640}
      >
        <List
          dataSource={library}
          data-testid="screens-library-list"
          renderItem={(t) => (
            <List.Item
              actions={[
                <Button
                  key="use"
                  size="small"
                  type="link"
                  data-testid={`screens-template-${t.templateId}`}
                  onClick={() => startFromLibrary(t.templateId)}
                >
                  Use this
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={t.name}
                description={
                  <Space direction="vertical" size={4}>
                    <span>{t.description}</span>
                    <Space size={4} wrap>
                      {t.flowTemplateId ? (
                        <Tag color="processing">fits {t.flowTemplateId}</Tag>
                      ) : (
                        <Tag>fits any flow</Tag>
                      )}
                      {t.kinds.map((k, i) => (
                        <Tag key={`${k}-${i}`} style={{ fontSize: 10.5 }}>
                          {k}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* Template-start: pick any existing version; its definition seeds a NEW config draft. */}
      <Modal
        title={
          <Space>
            <CopyOutlined />
            <span>Start from an existing config</span>
          </Space>
        }
        open={templateOpen}
        onCancel={() => setTemplateOpen(false)}
        footer={null}
        width={560}
      >
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5 }}>
            The picked version&apos;s journey is COPIED into a new config you then name — the
            original is never touched, and publishing the copy never supersedes it.
          </Paragraph>
          <Input
            allowClear
            autoFocus
            prefix={<SearchOutlined />}
            placeholder="Filter by config or flow id"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
          />
          <List
            size="small"
            dataSource={templateRows}
            style={{ maxHeight: 380, overflow: 'auto' }}
            locale={{ emptyText: 'No configs match the filter.' }}
            renderItem={(r) => (
              <List.Item
                actions={[
                  <Button key="use" size="small" type="link" onClick={() => startFromTemplate(r)}>
                    Use as template
                  </Button>,
                ]}
              >
                <Space size={8} wrap>
                  <Text className="ol-mono" style={{ fontSize: 12.5 }}>{r.configId}</Text>
                  <Tag>v{r.version}</Tag>
                  {statusTag(r.status)}
                  {r.flowId && (
                    <Text type="secondary" className="ol-mono" style={{ fontSize: 11.5 }}>
                      {r.flowId}
                    </Text>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </Space>
      </Modal>
    </div>
  );
}

function prettyOrRaw(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
